import {DataStatus, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Space, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useCreateQuickPresetMutation,
  useEditQuickPresetMutation,
  useGetQuickPresetQuery,
} from '@dms/api/settings';
import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, logError} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {QuickPresetFormValue, QuickPresetFormSchema} from '../types/QuickPresetFormValue';

export function QuickPresetForm() {
  const {id: quickPresetId} = useParams();
  const navigate = useNavigate();

  const isCreating = isNil(quickPresetId);

  const quickPresetQuery = useGetQuickPresetQuery(
    {id: quickPresetId || ''},
    {skip: !quickPresetId}
  );

  const isSystemPreset = quickPresetQuery.data?.isSystem ?? false;

  const [createQuickPreset, createQuickPresetStatus] = useCreateQuickPresetMutation();
  const [editQuickPreset, editQuickPresetStatus] = useEditQuickPresetMutation();

  const isSavingChanges = createQuickPresetStatus.isLoading || editQuickPresetStatus.isLoading;

  const defaultValues: Partial<QuickPresetFormValue> = isCreating
    ? {
        name: '',
        prompt: '',
      }
    : {
        name: quickPresetQuery.data?.name,
        prompt: quickPresetQuery.data?.prompt,
      };

  const handleSubmit: FormSubmitHandler<QuickPresetFormValue> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isCreating) {
      return await createQuickPreset({
        body: values,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.advertisingQuickPresets);
        })
        .catch(handleApiError);
    }

    if (isNil(quickPresetId)) {
      logError('quickPresetId is not defined');
      return;
    }

    await editQuickPreset({
      id: quickPresetId,
      body: values,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.advertisingQuickPresets);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <DataStatus
      isLoading={quickPresetQuery.isLoading}
      isError={quickPresetQuery.isError}
      minHeight="100vh"
    >
      <Form<QuickPresetFormValue>
        defaultValues={defaultValues}
        experimentalZodSchema={QuickPresetFormSchema}
        isDisabled={isSystemPreset}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => (
          <VStack spacing={4} width="50%">
            <FormField
              isRequired
              control={control}
              type="text"
              name="name"
              label={i18n.t('page.settings.labels.advertisingQuickPresets.name')}
              data-testid={testIds.settings.advertisingQuickPresetsDetail('quickPresetName')}
            />
            <FormField
              isRequired
              control={control}
              type="text"
              name="prompt"
              label={i18n.t('page.settings.labels.advertisingQuickPresets.prompt')}
              data-testid={testIds.settings.advertisingQuickPresetsDetail('quickPresetPrompt')}
            />

            <Space fillAvailable />

            <SettingsFooter
              actions={[
                {
                  isLoading: isSavingChanges,
                  type: 'form-button',
                  buttonType: 'reset',
                  control,
                  variant: 'secondary',
                  title: isCreating
                    ? i18n.t('general.actions.discard')
                    : i18n.t('general.actions.discardChanges'),
                  onClick: () =>
                    isCreating
                      ? navigateWithoutBlocker(-1)
                      : navigateWithoutBlocker(settingsRoutes.advertisingQuickPresets),
                  'data-testid': testIds.settings.advertisingQuickPresetsDetail('discard'),
                },
                {
                  isLoading: isSavingChanges,
                  type: 'form-button',
                  buttonType: 'submit',
                  control,
                  variant: 'primary',
                  title: isCreating
                    ? i18n.t('general.actions.save')
                    : i18n.t('general.actions.saveChanges'),
                  'data-testid': testIds.settings.advertisingQuickPresetsDetail('save'),
                },
              ]}
            />
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
